/**
 * 店舗在庫（STORE STOCK）モーダルの在庫取得。
 *
 * 「店舗在庫を確認する」ボタン（.js-showModalInventory）のクリックで、モーダル内の
 * .c-Variants__inventory[data-sku][data-store-id] から SKU を集め、theme-api の
 * App Proxy（/apps/theme-api/store-stock）から全店舗分の在庫ステータスを 1 回で取得して
 * 各セルに文言を書く。文言は clubhers 時代と同じ 4 種類なので、custom.js の店舗サマリ集約
 * （MutationObserver）はそのまま動く。
 *
 * - App Proxy が署名を付けるので fetch は素のまま（同一オリジン）
 * - ページ内で 1 回成功したら再取得しない（モーダルを開き直しても同じ結果を保持）
 * - theme-api が warming（起動直後）のときだけ 3 秒間隔で最大 3 回再試行
 * - それ以外の失敗（stale・非 200・タイムアウト）はセルを「読込中…」のまま残し、次に開いたときに再取得
 * - モーダルを開く処理自体は custom.js が担うが、バインド順に依存しないようここでも冪等に行う
 */
(function () {
  'use strict';

  var ENDPOINT = '/apps/theme-api/store-stock';
  var TIMEOUT_MS = 5000;
  var WARMING_RETRY_MS = 3000;
  var WARMING_MAX_ATTEMPTS = 3;
  var MAX_SKUS_PER_REQUEST = 100;
  var CELL_SELECTOR = '.c-Variants__inventory[data-sku][data-store-id]';
  var STATUS_TEXT = {
    in_stock: '在庫あり',
    low: '残りわずか',
    out_of_stock: '在庫なし',
    contact: 'お問い合わせ'
  };

  var loaded = false;
  var inflight = null;

  var internal = {
    sleep: function (ms) {
      return new Promise(function (resolve) { setTimeout(resolve, ms); });
    }
  };

  function statusText(status) {
    return Object.prototype.hasOwnProperty.call(STATUS_TEXT, status) ? STATUS_TEXT[status] : null;
  }

  function uniqueSkus(cells) {
    var seen = {};
    var skus = [];
    Array.prototype.forEach.call(cells, function (cell) {
      var sku = (cell.getAttribute('data-sku') || '').trim();
      if (sku === '' || seen[sku]) return;
      seen[sku] = true;
      skus.push(sku);
    });
    return skus;
  }

  function chunk(list, size) {
    var chunks = [];
    for (var i = 0; i < list.length; i += size) {
      chunks.push(list.slice(i, i + size));
    }
    return chunks;
  }

  function fetchWithTimeout(url) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    return window.fetch(url, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
      signal: controller.signal
    }).finally(function () { clearTimeout(timer); });
  }

  // 1 チャンク分を取得して stock マップを返す。取得できなければ null
  function fetchChunk(skus, attempt) {
    var url = ENDPOINT + '?skus=' + encodeURIComponent(skus.join(','));
    return fetchWithTimeout(url)
      .then(function (res) {
        if (!res.ok) return null;
        return res.json().then(function (json) {
          if (!json) return null;
          if (json.ready === false) {
            if (json.reason === 'warming' && attempt < WARMING_MAX_ATTEMPTS) {
              return internal.sleep(WARMING_RETRY_MS).then(function () {
                return fetchChunk(skus, attempt + 1);
              });
            }
            return null;
          }
          return json.stock && typeof json.stock === 'object' ? json.stock : null;
        });
      })
      .catch(function (err) {
        console.warn('[store-stock] fetch failed:', err);
        return null;
      });
  }

  // stock[sku][storeId] を対応するセルに書き、書き換えた数を返す
  function applyStock(stock, cells) {
    var written = 0;
    Array.prototype.forEach.call(cells, function (cell) {
      var sku = (cell.getAttribute('data-sku') || '').trim();
      var storeId = cell.getAttribute('data-store-id');
      var byStore = stock[sku];
      if (!byStore || !Object.prototype.hasOwnProperty.call(byStore, storeId)) return;
      var text = statusText(byStore[storeId]);
      if (text === null) return;
      cell.textContent = text;
      written++;
    });
    return written;
  }

  // 全チャンクが取得できたときだけ「成功」とみなす（部分的に書けた分は残し、次回開いたときに残りを取り直す）
  function load() {
    if (loaded) return Promise.resolve(true);
    if (inflight) return inflight;

    var cells = document.querySelectorAll(CELL_SELECTOR);
    var skus = uniqueSkus(cells);
    if (skus.length === 0) return Promise.resolve(false);

    inflight = Promise.all(chunk(skus, MAX_SKUS_PER_REQUEST).map(function (part) {
      return fetchChunk(part, 1);
    }))
      .then(function (results) {
        var merged = {};
        var complete = true;
        results.forEach(function (stock) {
          if (stock === null) { complete = false; return; }
          Object.keys(stock).forEach(function (sku) { merged[sku] = stock[sku]; });
        });
        applyStock(merged, cells);
        loaded = complete;
        return complete;
      })
      .catch(function (err) {
        console.warn('[store-stock] load failed:', err);
        return false;
      })
      .finally(function () { inflight = null; });

    return inflight;
  }

  function openModal() {
    var modal = document.querySelector('.product-form__variants.js-modal-inventory');
    if (!modal) return;
    modal.classList.add('is-show');
    document.body.classList.add('no-scroll');
  }

  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest('.js-showModalInventory') : null;
    if (!button) return;
    openModal();
    load();
  });

  window.storeStock = {
    load: load,
    _internal: internal
  };
  internal.uniqueSkus = uniqueSkus;
  internal.chunk = chunk;
  internal.statusText = statusText;
  internal.applyStock = applyStock;
})();
